import { useContext, useEffect, useRef, useState } from "react";
import styles from "./LoginRegisterForm.module.css";
import Input from "../Util/Input.jsx";
import axios from 'axios';
import Cookies from "js-cookie";
import AuthContext from "../../store/auth-context.jsx";
import { GlobalContext } from "../../store/global-context";
import { errorMessage } from "../Util/errorMessage";
import {
    ACCOUNT_CREATED, OTP_SENT, PASSWORD_RESET, RESEND_READY, SESSION_EXPIRED,
    checkOtp, checkRegister, checkReset, checkResetPhone, checkSignIn,
    otpRefusal, registerRefusal, resetPhoneRefusal, signInRefusal,
} from "./signInChecks";
import { Link, useNavigate } from "react-router-dom";

import { Dialog } from "primereact/dialog";
import { useDialogIsolation } from "../Util/useDialogIsolation";
import { useDocumentTitle } from "../Util/pageTitle";


axios.defaults.headers.post['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.post['X-CSRFToken'] = Cookies.get('csrftoken');

const RESEND_AFTER_SECONDS = 60; // the OTP resend countdown
// The page title for each mode, "<title> | RCMC RTA" (RCMC-07, ticket 11)
const MODE_TITLES = { "sign-in": "Sign in", register: "Create account", forgot: "Reset password" };
const csrfHeaders = () => ({ headers: { 'X-CSRFToken': Cookies.get('csrftoken') } });

const LoginRegisterForm = () => {
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const globalCtx = useContext(GlobalContext);
    // Each form, so a handler can focus one of its fields once the render it caused is done (see focusField)
    const loginFormRef = useRef();
    const signUpFormRef = useRef();
    const forgotFormRef = useRef(); // whichever step of Reset password is showing
    // RCMC-03 (ticket 08): the screen shows one mode at a time, held here instead of v1.2's CSS class toggling.
    // "forgot" = the Reset password dialog open over the Sign in form. Only the active form is rendered, so the
    // other one can never be read, tabbed into or autofilled.
    const [mode, setMode] = useState("sign-in");
    const modeRef = useRef(mode); // the mode on screen now, for replies that arrive after the Investor moved on
    modeRef.current = mode;
    const panel = mode === "register" ? "register" : "sign-in"; // which form is on screen
    useDocumentTitle(MODE_TITLES[mode]); // RCMC-07 (ticket 11): the tab names the mode
    const headingRef = useRef(); // the active form's h1, which takes focus on a mode switch
    const focusHeadingOnSwitch = useRef(false);
    const timerRef = useRef(); // the OTP resend countdown, so a mode switch or unmount can stop it
    const [signUpValidation,setSignupValidation] = useState(false);
    // RCMC-23: page behind inert while open, focus to the first field; RCMC-02: named close button
    const resetDialogIsolation = useDialogIsolation(mode === "forgot", "Reset password");
    // What the first step sent, kept for the OTP step: Resend posts it again and Verify adds the OTP to it
    // (ticket 09: v1.2 never stored the sign-in copy, so its Resend posted an empty body)
    const [signInFormData,setSignInFormData] = useState();
    const [signUpFormData,setSignUpFormData] = useState();
    const [signInValidation,setSignInValidation] = useState(false); // Sign in is at its OTP step
    const [noOfOTPResends,setNoOfOTPResends] = useState(0);
    const [timerVal,setTimerVal] = useState(0); // seconds until Resend OTP is available; 0 = available
    const [signInDisabled,setSignInDisabled] = useState(false);
    const [signUpDisabled,setSignUpDisabled] = useState(false);
    const [isPhoneNoVerified,setIsPhoneNoVerified] = useState(false); // Reset password is at its second step
    // RCMC-13/-14 (ticket 09): each form's errors, { <field name>: <text> }, shown next to the field (Input links
    // them); see signInChecks.js. v1.2 kept one shared object and used alert() for most errors.
    const [signInErrors, setSignInErrors] = useState({});
    const [signUpErrors, setSignUpErrors] = useState({});
    const [forgotErrors, setForgotErrors] = useState({});
    const pendingFocus = useRef(null);
    // The request this screen has out, if any (see startRequest)
    const pendingRequest = useRef(null);

    const navigateUser = (is_admin) => {
        if (authCtx.user.is_admin) {
            navigate("/operations/update-master-data");
        } else {
            navigate("/user/portfolio");
        }
    };

    useEffect(() => {
        if(authCtx.user){
            authCtx.updateToken();
            navigateUser();
        }
    },[authCtx.user])

    // RCMC-22 (ticket 10): arriving here because the session ended (auth-context's expireSession) → say so in the
    // status region, once. An ordinary Sign out never sets it. Runs after the screen being left has unmounted,
    // so nothing that screen clears on its way out can take the message with it.
    const { sessionExpired, clearSessionExpired } = authCtx;
    useEffect(() => {
        if (!sessionExpired) return;
        globalCtx.raiseAlert(SESSION_EXPIRED, "success");
        clearSessionExpired();
    }, [sessionExpired, clearSessionExpired, globalCtx])

    // Switch between Sign in and Create Account. The form being left unmounts and loses what was typed, so its
    // errors and OTP stage are dropped with it; focus then moves to the new form's heading (RCMC-03).
    // Switching to the mode already on screen does nothing: a late "registered" reply must not reset a Sign in
    // the Investor has already started.
    const switchMode = (nextMode) => {
        if (nextMode === modeRef.current) return;
        clearInterval(timerRef.current);
        setTimerVal(0);
        setSignInErrors({});
        setSignUpErrors({});
        setSignupValidation(false);
        setSignInValidation(false);
        dropRequests();
        setNoOfOTPResends(0);
        // The messages were about the form being left ("OTP sent", its errors)
        globalCtx.clearAlert();
        globalCtx.clearStatus();
        focusHeadingOnSwitch.current = true;
        setMode(nextMode);
    }

    // After a switch has rendered the new form, put focus on its heading. Not on first load, and not when the
    // Reset password dialog closes: the dialog returns focus to its opener (ticket 04).
    useEffect(() => {
        const switched = focusHeadingOnSwitch.current;
        focusHeadingOnSwitch.current = false;
        if (switched) headingRef.current?.focus();
    }, [mode]);

    // Stop the OTP countdown when the screen goes away (e.g. after signing in), and take its messages with it:
    // "OTP sent" or "You can now resend the OTP" must not greet the Investor on their Portfolio
    useEffect(() => () => {
        clearInterval(timerRef.current);
        pendingRequest.current = null; // a reply still out has nothing left to update
        globalCtx.clearAlert();
        globalCtx.clearStatus();
    }, [globalCtx]);

    // Focus a form's field after the render a handler caused, so its error (or its new OTP field) is in the page
    // and in the field's description when focus lands (RCMC-13/-14). `select`: also select its text, so what the
    // Investor types next replaces it — a full OTP field (maxlength 6) would otherwise drop every new digit.
    const focusField = (formRef, name, { select = false } = {}) => {
        pendingFocus.current = () => {
            const field = formRef.current?.elements.namedItem(name);
            field?.focus();
            if (select) field?.select?.();
        };
    };
    useEffect(() => {
        const focus = pendingFocus.current;
        pendingFocus.current = null;
        focus?.();
    });

    // One request at a time, and only for where the Investor still is (review of ticket 09). A second press of
    // Resend, Verify or Send OTP while the first is out is ignored: a second OTP would leave only the newest one
    // working, and a second Verify & Register fails after the first succeeded. A reply acts only if nothing
    // dropped its request meanwhile: switching form, or opening or closing Reset password (dropRequests), so a
    // late "OTP sent" never lands on a form the Investor has left.
    const busy = () => pendingRequest.current !== null;
    const startRequest = () => (pendingRequest.current = {});
    /** A reply has come for `request`: true if it still counts */
    const finishRequest = (request) => {
        const current = pendingRequest.current === request;
        if (current) pendingRequest.current = null;
        return current;
    };
    // Drop whatever is out: its reply will be ignored, so no button may stay at "Please wait..." for it
    const dropRequests = () => {
        pendingRequest.current = null;
        setSignInDisabled(false);
        setSignUpDisabled(false);
    };

    // A form's errors: each is shown next to its field by the form's state; here they are also announced, all
    // together in the alert region, and the first field in error takes focus. False when there is nothing to report.
    const reportErrors = (errors, formRef) => {
        const names = Object.keys(errors);
        if (names.length === 0) return false;
        globalCtx.raiseAlert(Object.values(errors).join(" "), "error");
        focusField(formRef, names[0], { select: true });
        return true;
    };

    // A refused request: on the field it concerns when `fieldErrors` names one, otherwise the readable reason
    // in the alert region (no response, HTTP status, …)
    const reportRefusal = (error, fieldErrors, setErrors, formRef) => {
        setErrors(fieldErrors);
        if (!reportErrors(fieldErrors, formRef)) globalCtx.raiseAlert(errorMessage(error), "error");
    };

    // An OTP has just been sent: announce it once (the status region, RCMC-14) and put the Investor in its field
    const otpSent = (formRef) => {
        globalCtx.clearAlert();
        globalCtx.raiseAlert(OTP_SENT, "success");
        focusField(formRef, "otp");
    };

    // Create Account submits twice: details (→ OTP sent), then, at the OTP step, the OTP (Verify & Register is the
    // submit button then, so Enter in the OTP field verifies)
    const onSignUpSubmitHandler = (event) => {
        event.preventDefault();
        if (busy()) return;
        const form = event.currentTarget;
        if (signUpValidation) {
            verifyRegister(form);
            return;
        }
        const data = Object.fromEntries(new FormData(form).entries());
        const errors = checkRegister(data);
        setSignUpErrors(errors);
        if (reportErrors(errors, signUpFormRef)) return;
        globalCtx.clearAlert();
        setSignUpDisabled(true);
        setSignUpFormData(data);
        const request = startRequest();
        axios.post("/api/user/register",data,csrfHeaders()).then(() => {
            if (!finishRequest(request)) return;
            setSignupValidation(true);
            setNoOfOTPResends(0);
            startTimer();
            setSignUpDisabled(false);
            otpSent(signUpFormRef);
        }, (error) => {
            if (!finishRequest(request)) return;
            setSignUpDisabled(false);
            reportRefusal(error, registerRefusal(error), setSignUpErrors, signUpFormRef);
        });
    }

    const verifyRegister = (form) => {
        const otp = form.elements.namedItem("otp").value;
        const errors = checkOtp(otp);
        setSignUpErrors(errors);
        if (reportErrors(errors, signUpFormRef)) return;
        globalCtx.clearAlert();
        const request = startRequest();
        axios.post("/api/user/otp/verify/register",{otp},csrfHeaders()).then(() => {
            // The account exists now, so this is said even if the Investor has already gone back to Sign in
            finishRequest(request);
            switchMode("sign-in"); // no-op if the Investor already went back to Sign in
            globalCtx.raiseAlert(ACCOUNT_CREATED, "success"); // after the switch, which clears the old messages
        }, (error) => {
            if (!finishRequest(request)) return; // the Investor has left Create Account and its OTP field
            reportRefusal(error, otpRefusal(error), setSignUpErrors, signUpFormRef);
        });
    }

    // Sign in submits twice: phone + password (→ OTP sent), then, at the OTP step, the OTP. v1.2's Verify was a
    // separate type="button", so Enter in the OTP field re-sent the first step without the (disabled) phone and
    // password.
    const onLoginSubmitHandler = (event) => {
        event.preventDefault();
        if (busy()) return;
        const form = event.currentTarget;
        if (signInValidation) {
            verifySignIn(form);
            return;
        }
        const data = Object.fromEntries(new FormData(form).entries());
        const errors = checkSignIn(data);
        setSignInErrors(errors);
        // A failed check returns before the button is disabled (v1.2 left it locked at "Please wait...")
        if (reportErrors(errors, loginFormRef)) return;
        globalCtx.clearAlert();
        setSignInDisabled(true);
        const request = startRequest();
        axios.post("/api/user/login",data,csrfHeaders()).then(() => {
            if (!finishRequest(request)) return;
            setSignInFormData(data);
            setSignInValidation(true);
            setNoOfOTPResends(0);
            startTimer();
            setSignInDisabled(false);
            otpSent(loginFormRef);
        }, (error) => {
            if (!finishRequest(request)) return;
            setSignInDisabled(false);
            reportRefusal(error, signInRefusal(error), setSignInErrors, loginFormRef);
        });
    }

    const verifySignIn = (form) => {
        const otp = form.elements.namedItem("otp").value;
        const errors = checkOtp(otp);
        setSignInErrors(errors);
        if (reportErrors(errors, loginFormRef)) return;
        globalCtx.clearAlert();
        // Success: the session starts and the effect on authCtx.user moves on to the Investor's first page
        const request = startRequest();
        authCtx.loginUser({ ...signInFormData, otp }).then(() => finishRequest(request), (error) => {
            if (!finishRequest(request)) return;
            reportRefusal(error, otpRefusal(error), setSignInErrors, loginFormRef);
        });
    }

    // Resend OTP (RCMC-14): posts exactly what the first step posted. v1.2's rule stays: one resend per OTP step.
    const resendOtp = () => {
        if (busy()) return;
        const register = panel === "register";
        const formRef = register ? signUpFormRef : loginFormRef;
        const request = register
            ? axios.post("/api/user/register",signUpFormData,csrfHeaders())
            : axios.post("/api/user/login",signInFormData,csrfHeaders());
        const sent = startRequest();
        request.then(() => {
            if (!finishRequest(sent)) return;
            setNoOfOTPResends(1);
            (register ? setSignUpErrors : setSignInErrors)({}); // an error about the old OTP is out of date
            const otp = formRef.current?.elements.namedItem("otp");
            if (otp) otp.value = ""; // the old OTP no longer works; make room for the new one
            otpSent(formRef);
        }, (error) => {
            if (!finishRequest(sent)) return;
            globalCtx.raiseAlert(errorMessage(error), "error");
        });
    }

    // The resend countdown: shown on screen, never announced while it runs; its end is announced once (RCMC-14)
    function startTimer() {
        let seconds = RESEND_AFTER_SECONDS;
        setTimerVal(seconds);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            seconds -= 1;
            setTimerVal(seconds);
            if (seconds <= 0) {
                clearInterval(timerRef.current);
                globalCtx.raiseAlert(RESEND_READY, "success");
            }
        }, 1000);
    }

    // Reset password, step 1: send an OTP to the phone number's account
    const verifyNumber = (event) => {
        event.preventDefault();
        if (busy()) return;
        const data = Object.fromEntries(new FormData(event.currentTarget).entries());
        const errors = checkResetPhone(data);
        setForgotErrors(errors);
        if (reportErrors(errors, forgotFormRef)) return;
        globalCtx.clearAlert();
        const request = startRequest();
        axios.get("/api/user/verify-user?phone_no="+data.phone_no).then(() => {
            if (!finishRequest(request)) return;
            setIsPhoneNoVerified(true);
            otpSent(forgotFormRef);
        }, (error) => {
            if (!finishRequest(request)) return;
            reportRefusal(error, resetPhoneRefusal(error), setForgotErrors, forgotFormRef);
        });
    }

    // Reset password, step 2: OTP + the new password
    const forgotPassword = (event) => {
        event.preventDefault();
        if (busy()) return;
        const formData = new FormData(event.currentTarget);
        const errors = checkReset(Object.fromEntries(formData.entries()));
        setForgotErrors(errors);
        if (reportErrors(errors, forgotFormRef)) return;
        globalCtx.clearAlert();
        const request = startRequest();
        axios.post("/api/user/reset-password",formData,csrfHeaders()).then(() => {
            if (!finishRequest(request)) return;
            setIsPhoneNoVerified(false);
            restartSignIn();
            setMode("sign-in");
            globalCtx.raiseAlert(PASSWORD_RESET, "success");
        }, (error) => {
            if (!finishRequest(request)) return;
            reportRefusal(error, otpRefusal(error), setForgotErrors, forgotFormRef);
        });
    }

    // Reset password opens over Sign in with a clean slate, and anything Sign in still had out is dropped
    const openForgot = () => {
        dropRequests();
        setForgotErrors({});
        setIsPhoneNoVerified(false);
        setMode("forgot");
    }

    // Reset password closed by the Investor (close button, Escape): its messages ("OTP sent", its errors) and any
    // reply still out go with it. (A successful reset closes it through forgotPassword, which announces the reset.)
    const closeForgot = () => {
        dropRequests();
        globalCtx.clearAlert();
        globalCtx.clearStatus();
        setMode("sign-in");
    }

    // After a password reset, Sign in starts again from its first step: an OTP it is waiting on was sent for the
    // old password, which also leaves the password field. The phone number stays.
    const restartSignIn = () => {
        clearInterval(timerRef.current);
        setTimerVal(0);
        setSignInValidation(false);
        setSignInFormData(undefined);
        setSignInErrors({});
        setNoOfOTPResends(0);
        const password = loginFormRef.current?.elements.namedItem("password");
        if (password) password.value = "";
    }

    // Ticket 22 (Q35, review): Sign In / Register at "Please wait..." stay enabled, so focus stays on them while the request
    // runs (v1.2's native `disabled` dropped it to <body>); they say they are busy, and the submit handlers already ignore
    // a press while a request is out (busy() above)
    const busyWhile = (busyNow) => (busyNow ? { "aria-disabled": "true", "aria-busy": "true" } : {});

    // The OTP step's resend control. Resend OTP is a native button, disabled while the countdown runs; the
    // countdown beside it is plain text (visible, read in browse mode, not live).
    const resendControl = noOfOTPResends === 0 ? (
        <div className={styles["resend"]}>
            <button type="button" className={styles["resend-button"]} disabled={timerVal > 0} onClick={resendOtp}>Resend OTP</button>
            {timerVal > 0 && <span> in <span className={styles["countdown-seconds"]}>{timerVal}</span> seconds</span>}
        </div>
    ) : <div className={styles["resend"]}>OTP sent again.</div>;

    // The blue panel beside the form slides between two halves, one per mode. The half for the other mode sits
    // off-screen: inert and hidden, so its switch button is out of reach (RCMC-03).
    const offscreenUnless = (forPanel) => (panel === forPanel ? {} : { inert: "", "aria-hidden": "true" });

    // RCMC-15 (ticket 12): the public help page, from whichever form is showing (only one is rendered). Inside the card:
    // below it there is no room (the card is 95vh tall, centred in a 100vh screen).
    const accessibilityHelpLink = <Link to="/accessibility-help" className={styles["help-link"]}>Accessibility help</Link>;

    return (
        // RCMC-09 (ticket 11): the screen is the page's one main region (v1.2: a plain div)
        <main className={styles["login-register"]}>
            <div className={`${styles["container"]} ${panel === "register" ? styles["right-panel-active"] : ""}`}>
                {panel === "register" && (
                <div className={`${styles["form-container"]} ${styles["sign-up-container"]}`}>
                    {/* noValidate: the form reports its own errors (RCMC-13); Chrome's bubble would block type=email */}
                    <form ref={signUpFormRef} action="#" onSubmit={onSignUpSubmitHandler} id="sign-up-form" noValidate>
                        <h1 ref={headingRef} tabIndex={-1}>Create Account</h1>
                        <Input label="Name" type="text" name="name" autoComplete="name" required error={signUpErrors.name} disabled={signUpValidation} />
                        <Input label="PAN No" type="text" name="pan_no" required error={signUpErrors.pan_no} disabled={signUpValidation} />
                        <Input label="Email" type="email" name="email" autoComplete="email" required error={signUpErrors.email} disabled={signUpValidation} />
                        <Input label="Phone Number" type="tel" name="phone_no" autoComplete="tel-national" inputMode="numeric" maxLength={10} required error={signUpErrors.phone_no} disabled={signUpValidation} />
                        <Input label="Password" type="password" name="password" autoComplete="new-password" required error={signUpErrors.password} disabled={signUpValidation} />
                        <Input label="Re-Enter Password" type="password" name="re_enter_password" autoComplete="new-password" required error={signUpErrors.re_enter_password} disabled={signUpValidation} />
                        {signUpValidation ? (
                            <>
                                <Input label="6-digit OTP" hint={OTP_SENT} type="text" name="otp" autoComplete="one-time-code" inputMode="numeric" maxLength={6} required error={signUpErrors.otp} />
                                {resendControl}
                                <button type="submit">Verify & Register</button>
                            </>) : <button {...busyWhile(signUpDisabled)}>{signUpDisabled ? "Please wait..." :"Register"}</button>}
                        {accessibilityHelpLink}
                    </form>
                </div>
                )}
                {panel === "sign-in" && (
                <div className={`${styles["form-container"]} ${styles["sign-in-container"]}`}>
                    <form ref={loginFormRef} action="#" onSubmit={onLoginSubmitHandler} id="sign-in-form" noValidate>
                        <h1 ref={headingRef} tabIndex={-1}>Sign in</h1>
                        <span>using your phone number</span>
                        <Input label="Phone Number" type="tel" name="phone_no" autoComplete="tel-national" inputMode="numeric" maxLength={10} required error={signInErrors.phone_no} disabled={signInValidation} />
                        <Input label="Password" type="password" name="password" autoComplete="current-password" required error={signInErrors.password} disabled={signInValidation} />
                        {signInValidation ? (
                            <>
                                <Input label="6-digit OTP" hint={OTP_SENT} type="text" name="otp" autoComplete="one-time-code" inputMode="numeric" maxLength={6} required error={signInErrors.otp} />
                                {resendControl}
                                <button type="submit">Verify & Sign In</button>
                            </>) : <button {...busyWhile(signInDisabled)}>{signInDisabled? "Please wait..." :"Sign In"}</button>}
                        <button type="button" className={styles["forgot-password"]} onClick={openForgot}>Forgot Password?</button>
                        {accessibilityHelpLink}
                    </form>
                </div>
                )}
                <div className={styles["overlay-container"]}>
                    <div className={styles["overlay"]}>
                        {/* The greetings are decoration: hidden from screen readers and no longer h1s (one h1 per mode) */}
                        <div className={`${styles["overlay-panel"]} ${styles["overlay-left"]}`} {...offscreenUnless("register")}>
                            <div className={styles["greeting"]} aria-hidden="true">Welcome Back!</div>
                            <p aria-hidden="true">To keep connected with us please login with your personal info</p>
                            <button className={styles["ghost"]} tabIndex={panel === "register" ? undefined : -1} onClick={() => switchMode("sign-in")}>Sign In</button>
                        </div>
                        <div className={`${styles["overlay-panel"]} ${styles["overlay-right"]}`} {...offscreenUnless("sign-in")}>
                            <div className={styles["greeting"]} aria-hidden="true">Dear, Investor!</div>
                            <p aria-hidden="true">Enter your personal details and start journey with us</p>
                            <button className={styles["ghost"]} tabIndex={panel === "sign-in" ? undefined : -1} onClick={() => switchMode("register")}>Sign Up</button>
                        </div>
                    </div>
                </div>

                <Dialog header="Forgot Password" visible={mode === "forgot"} style={{ width: "30vw" }} onHide={closeForgot} {...resetDialogIsolation}>
                        {!isPhoneNoVerified && 
                            <form ref={forgotFormRef} onSubmit={verifyNumber} noValidate style={{display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center"}}>
                                <Input label="Phone Number" type="tel" name="phone_no" autoComplete="tel-national" inputMode="numeric" maxLength={10} required error={forgotErrors.phone_no} />
                                <button className={styles['forgot-password-button']}>Send OTP</button>
                            </form>
                        }
                        {isPhoneNoVerified && 
                        <form ref={forgotFormRef} onSubmit={forgotPassword} noValidate style={{display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center"}}>
                            <Input label="6-digit OTP" hint={OTP_SENT} type="text" name="otp" autoComplete="one-time-code" inputMode="numeric" maxLength={6} required error={forgotErrors.otp} />
                            <Input label="Password" type="password" name="password" autoComplete="new-password" required error={forgotErrors.password} />
                            <Input label="Re-Enter Password" type="password" name="re_enter_password" autoComplete="new-password" required error={forgotErrors.re_enter_password} />
                            <button className={styles['forgot-password-button']}>Reset Password</button>
                        </form>}
                </Dialog>
            </div>
        </main>
    );
}

export default LoginRegisterForm;